import type { LegalActions } from '@tctp/protocol';
import { type FormEvent, useMemo, useState } from 'react';
import { useShortcuts } from '../useShortcuts';

const clamp = (n: number, min: number, max: number): number => Math.min(max, Math.max(min, n));

/** PokerNow's raise flow: "Your bet" with the big-blind count, quick amounts, a slider with − and +, BACK and RAISE. */
export function RaisePanel({ legal, potTotal, currentBet, bigBlind, onRaise, onBack }: {
  legal: LegalActions; potTotal: number; currentBet: number; bigBlind: number; onRaise: (to: number) => void; onBack: () => void;
}) {
  const min = legal.minRaiseTo ?? 0;
  const max = legal.maxRaiseTo ?? min;
  const [text, setText] = useState(String(min));
  const typed = Math.floor(Number(text) || 0);
  const amount = clamp(typed, min, max);   // what the slider and ± work from
  // A typed amount outside the legal range is refused, not silently turned into a shove or a minimum raise.
  const problem = typed > max ? `Not enough chips: you can bet up to ${max}` : typed < min ? `Minimum is ${min}` : null;
  const potAfterCall = potTotal + (legal.callAmount ?? 0);
  const quick = [
    { label: 'MIN RAISE', to: min },
    { label: '1/2 POT', to: currentBet + Math.round(potAfterCall / 2) },
    { label: '3/4 POT', to: currentBet + Math.round((3 * potAfterCall) / 4) },
    { label: 'POT', to: currentBet + potAfterCall },
    { label: 'ALL IN', to: max },
  ];
  const set = (n: number): void => setText(String(clamp(n, min, max)));
  const bb = amount / bigBlind;
  const label = currentBet === 0 ? 'BET' : 'RAISE';
  const shortcuts = useMemo(() => ({ Enter: problem === null ? () => onRaise(amount) : undefined }), [onRaise, amount, problem]);
  useShortcuts(shortcuts);
  function submit(e: FormEvent): void {
    e.preventDefault();
    if (problem === null) onRaise(amount);
  }
  return (
    <form className="raise-panel" onSubmit={submit} noValidate>
      <label className="your-bet">
        <span>Your bet</span>
        <input type="number" inputMode="numeric" aria-label="Your bet" value={text} min={min} max={max} onChange={e => setText(e.target.value)} autoFocus aria-invalid={problem !== null} />
        <span className="bb">{Number.isInteger(bb) ? bb : bb.toFixed(1)} BB</span>
        {problem && <span className="bet-problem" role="alert">{problem}</span>}
      </label>
      <div className="raise-controls">
        <div className="quick">
          {quick.map(q => <button key={q.label} type="button" onClick={() => set(q.to)}>{q.label}</button>)}
        </div>
        <div className="slider-row">
          <button type="button" aria-label="−" onClick={() => set(amount - bigBlind)}>−</button>
          <input type="range" aria-label="Raise amount" min={min} max={max} step={1} value={amount} onChange={e => set(Number(e.target.value))} />
          <button type="button" aria-label="+" onClick={() => set(amount + bigBlind)}>+</button>
        </div>
      </div>
      <button type="button" className="act-btn back" onClick={onBack}><span className="hint" aria-hidden="true">ESC</span>BACK</button>
      <button type="submit" className="btn green confirm" disabled={problem !== null}>{label}</button>
    </form>
  );
}
